import { useQueryClient } from "@tanstack/react-query";
import { VenetianMask } from "lucide-react";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { buildResumeResponses } from "@/features/pulse-surveys/components/self-check-in-dialog-state";
import {
  useStartSelfCheckIn,
  useSubmitSelfCheckInResponse,
} from "@/features/pulse-surveys/hooks/use-self-check-in";
import type { SelfCheckInOverview } from "@/features/pulse-surveys/schemas/self-check-in.schema";
import { FreeTextControl } from "@/shared/components/question-controls/free-text-control";
import { MoodControl } from "@/shared/components/question-controls/mood-control";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/cn";
import {
  MOOD_AFFIRMATIONS,
  MOOD_DISPLAY,
  MOOD_FOLLOW_UPS,
  MOOD_OPTIONS,
  type MoodOptionId,
  moodFromFollowUpId,
} from "@/shared/pulse/mood/mood-display";

type Step = "mood" | "follow-up";

interface Props {
  workspaceId: string;
  overview: SelfCheckInOverview;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

export function SelfCheckInDialog({
  workspaceId,
  overview,
  open,
  onOpenChange,
  children,
}: Props): ReactNode {
  const qc = useQueryClient();
  const start = useStartSelfCheckIn(workspaceId);
  const submit = useSubmitSelfCheckInResponse(workspaceId, overview.deliveryId);
  const [step, setStep] = useState<Step>("mood");
  // reason: activeMoodId is transient dialog-wizard state, not URL state.
  const [activeMoodId, setActiveMoodId] = useState<MoodOptionId | null>(null);
  const [followUpText, setFollowUpText] = useState("");
  const [resumePrompt, setResumePrompt] = useState<string | null>(null);

  function resetState(fromOverview: SelfCheckInOverview): void {
    setFollowUpText("");

    if (fromOverview.availability === "available") {
      setStep("mood");
      setActiveMoodId(null);
      setResumePrompt(null);
      return;
    }

    const snapshot = Array.isArray(fromOverview.questionsSnapshot)
      ? (fromOverview.questionsSnapshot as Array<{ id?: string; text?: string }>)
      : [];
    const prompt = snapshot[1]?.text ?? null;
    setStep(prompt ? "follow-up" : "mood");
    setActiveMoodId(moodFromFollowUpId(snapshot[1]?.id));
    setResumePrompt(prompt);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen) {
      resetState(overview);
      onOpenChange(true);
      return;
    }

    onOpenChange(false);
    void qc.invalidateQueries({ queryKey: ["pulse-surveys", workspaceId, "me"] });
    void qc.invalidateQueries({ queryKey: ["onboarding", workspaceId] });
  }

  function selectMood(mood: MoodOptionId): void {
    setActiveMoodId(mood);
    setResumePrompt(null);
    setStep("follow-up");
  }

  /// Eager finish: today is marked done in the overview cache and the dialog
  /// closes immediately; the request settles in the background (the submit
  /// hooks invalidate on settle, which reconciles with the shore). On error a
  /// toast surfaces and invalidation restores the real state.
  function finish(skipFollowUp: boolean): void {
    if (!activeMoodId) {
      toast.error("Pick a mood to continue.");
      return;
    }
    const mood = activeMoodId;
    const wasResume = overview.availability === "in_progress";
    const trimmedFollowUp = followUpText.trim();
    const overviewKey = ["pulse-surveys", workspaceId, "me", "self-check-in"] as const;

    void qc.cancelQueries({ queryKey: overviewKey });
    qc.setQueryData<SelfCheckInOverview>(overviewKey, (current) =>
      current ? { ...current, availability: "done_today", todayMood: mood } : current,
    );
    onOpenChange(false);
    void qc.invalidateQueries({ queryKey: ["onboarding", workspaceId] });

    const send = async (): Promise<void> => {
      if (wasResume) {
        const requests = buildResumeResponses(overview, mood, followUpText, skipFollowUp);
        for (const request of requests) {
          await submit.mutateAsync(request);
        }
      } else {
        await start.mutateAsync({
          mood,
          followUpText: !skipFollowUp && trimmedFollowUp ? trimmedFollowUp : undefined,
        });
      }
    };
    send().catch((error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : "Couldn't save your check-in. Try again.",
      );
      void qc.invalidateQueries({ queryKey: ["pulse-surveys", workspaceId, "me"] });
    });
  }

  const prompt = activeMoodId
    ? MOOD_FOLLOW_UPS[activeMoodId].text
    : (resumePrompt ?? "Anything you want to share?");
  const affirmation = activeMoodId ? MOOD_AFFIRMATIONS[activeMoodId] : "Thanks for checking in.";
  const isSaving = start.isPending || submit.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md overflow-hidden p-0 sm:max-w-lg" showCloseButton>
        <div className="relative border-b border-border/70 bg-pulse-soft px-6 py-6">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 text-left">
            <span
              className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-soft-bg text-text-brand-strong ring-1 ring-inset ring-brand-soft-border shadow-xs"
              aria-hidden="true"
            >
              {step === "follow-up" && activeMoodId ? (
                <span
                  key={activeMoodId}
                  className="animate-in fade-in-0 zoom-in-75 text-xl leading-none duration-200 motion-reduce:animate-none"
                >
                  {MOOD_DISPLAY[activeMoodId].emoji}
                </span>
              ) : (
                <VenetianMask className="size-5" />
              )}
            </span>
            <div className="space-y-0.5">
              <DialogTitle className="text-xl tracking-tight">Daily check-in</DialogTitle>
              <DialogDescription className="text-text-secondary">
                Always anonymous. Never tied to your name.
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>

        <div className="px-6 py-5">
          <div className="min-h-[280px]">
            <div
              key={`${step}-${activeMoodId ?? "none"}`}
              className={cn(
                "animate-in fade-in-0 duration-200 motion-reduce:animate-none",
                step === "mood" ? "slide-in-from-left-2" : "slide-in-from-right-2",
              )}
            >
              {step === "mood" ? (
                <div className="space-y-5">
                  <div>
                    <p className="text-lg font-medium text-text-primary">How's your mood today?</p>
                  </div>

                  <MoodControl
                    options={MOOD_OPTIONS}
                    value={activeMoodId}
                    onChange={(next) => selectMood(next as MoodOptionId)}
                    size="lg"
                  />
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="space-y-3">
                    <div aria-live="polite">
                      <p className="text-lg font-medium text-text-primary">{prompt}</p>
                      <p className="mt-1 text-sm text-text-tertiary">{affirmation}</p>
                    </div>
                  </div>

                  <FreeTextControl
                    value={followUpText}
                    onChange={setFollowUpText}
                    rows={5}
                    max={1200}
                    placeholder="Share as much or as little as you like."
                    privacyNote="Optional"
                  />

                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button variant="ghost" onClick={() => setStep("mood")} disabled={isSaving}>
                      Back
                    </Button>
                    <Button variant="ghost" onClick={() => void finish(true)} disabled={isSaving}>
                      Skip
                    </Button>
                    <Button onClick={() => void finish(false)} disabled={isSaving}>
                      {isSaving ? "Saving..." : "Done"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
